import { useEffect, useRef, useState } from "react";

import { Button, Container, PortalLayout, Stack, Typography } from "../../design-system";
import {
  clearFragment,
  getSessionToken,
  isValidTokenFormat,
  readTokenFromLocation,
  redeemMagicLink,
} from "../../portal-auth";
import { redeemContent } from "./content";

/**
 * Sets `Referrer-Policy: no-referrer` for as long as /redeem is mounted
 * (TG-35 standard, AC-31). The portal is one static `index.html` served for
 * every route (navigationFallback), so there is no per-route HTML
 * document to carry this as a `<meta>` tag server-side; registering it
 * here, scoped to this component's own lifetime, reaches the same end
 * without extending `src/routes/reserved-paths.json`'s generator (a
 * schema and Python-ported trunk-check shared with two other features,
 * ADR-20260824-gps6) to support a per-route response header neither
 * needs today. The Continue action's own fetch additionally passes
 * `referrerPolicy: "no-referrer"` directly (portal-auth/api.ts), so the
 * one request this page actually makes is covered twice over.
 */
function useNoReferrerPolicy(): void {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

type BareViewState = "interstitial" | "couldNotComplete" | "throttled" | "invalidFormat";

export interface RedeemPageProps {
  /** A redeemed token for a renewal whose working copy is still live:
   * navigate into the authenticated app. */
  onRedeemed: () => void;
  /** A redeemed token for a submitted renewal whose working copy has been
   * purged (AC-3's second sentence): navigate to the bare renewal-status
   * view. */
  onReadOnly: () => void;
  /** ADR-20261015-6w29 (AC-9): the presented token was spent or unknown,
   * but the browser already holds a live session -- no new session is
   * created; navigate to that session's screen 2, exactly where Back
   * after sign-in goes (see this component's own `useEffect` below for
   * the no-token case, which reaches this same callback when a session
   * is already live). */
  onActiveSession: () => void;
  /** Every not-found variant (AC-6, AC-7, AC-8's "any other 4xx"):
   * navigate to the dedicated link-no-longer-works view. */
  onNotFound: () => void;
  /** ADR-20261016-f8ws (design state 1.5, AC-8): the `invalidFormat`
   * view's one action -- opens the sign-in view, the same destination
   * `LinkUnavailablePage.onGetNewLink` navigates to, never a resend
   * action and never a redeem call. */
  onGetNewLink: () => void;
}

/**
 * /redeem (design state 1.3, AC-18, AC-23, AC-29), rebuilt in
 * `PortalLayout` with TG-43 copy. The GET that serves this view reads
 * the token from the URL fragment with no network call at all (see
 * portal-auth/fragment.ts) -- it touches no token record, changes no token
 * record, establishes no session and writes no audit event. Only the
 * Continue action's POST spends the token. Rendering with scripts enabled
 * and no user input sends no POST within 60 seconds, because nothing here
 * runs on a timer: the only path to a POST is this component's own
 * onClick.
 *
 * Four states render in place on this one route (ADR-20261008-181w,
 * extended by ADR-20261016-f8ws): `interstitial` (the GET view, before
 * Continue is pressed), `couldNotComplete` (AC-25, a 5xx confirm response
 * or no response), `throttled` (AC-8/AC-26, a 429 confirm response) and
 * `invalidFormat` (design state 1.5, AC-8 -- a present token that fails
 * the client-side format check, decided on mount with no confirm POST
 * ever made). Every other outcome navigates away via the caller-supplied
 * callback rather than rendering here. `interstitial` and
 * `couldNotComplete` are shown inside `PortalLayout` (AC-29: both are
 * among the views in AC-21 to AC-25 that carry the "Renewal portal"
 * header; only `couldNotComplete` omits the help line). `throttled` and
 * `invalidFormat` are also shown in `PortalLayout`, with the help line
 * (ADR-20261008-hsvr amendment 2026-10-11, and ADR-20261016-f8ws
 * respectively).
 */
export function RedeemPage({
  onRedeemed,
  onReadOnly,
  onActiveSession,
  onNotFound,
  onGetNewLink,
}: Readonly<RedeemPageProps>) {
  useNoReferrerPolicy();
  // ADR-20261015-6w29 (AC-7): read from the fragment OR the query string --
  // whichever form the emailed link used -- with no network call either way.
  const [token] = useState<string | null>(() =>
    readTokenFromLocation(window.location.hash, window.location.search),
  );
  const [submitting, setSubmitting] = useState(false);
  // ADR-20261016-f8ws (design state 1.5): a PRESENT token that fails the
  // client-side format check starts -- and stays -- on `invalidFormat`.
  // Computed once, from the same initial read `token` itself uses, so
  // there is never a flash of the interstitial before switching: this
  // state is never reached through `setView`, only through this
  // initializer, and `handleContinue` is never wired to any control the
  // `invalidFormat` branch renders, so no redeem call is reachable from it.
  const [view, setView] = useState<BareViewState>(() =>
    token !== null && !isValidTokenFormat(token) ? "invalidFormat" : "interstitial",
  );
  const missingTokenHandled = useRef(false);

  // AC-8: a magic-link URL whose fragment/query is missing, empty or
  // malformed into no token shows the link-no-longer-works view directly.
  // There is no token to confirm, so no interstitial and no POST.
  //
  // AC-9: EXCEPT when the browser already holds a live session -- the one
  // way this effect runs with no token is Back navigating to this route
  // after a successful sign-in (the token was stripped from the address
  // bar the moment Continue was pressed, so there is never a token here to
  // re-read). That case shows screen 2, never a tokenless Continue button
  // and never the not-found view. Checked client-side only, against the
  // in-memory session token -- no server call, matching AC-7/AC-23's "no
  // network call" rule for every GET this route serves.
  useEffect(() => {
    if (token !== null || missingTokenHandled.current) {
      return;
    }
    missingTokenHandled.current = true;
    clearFragment();
    if (getSessionToken() !== null) {
      onActiveSession();
      return;
    }
    onNotFound();
  }, [token, onActiveSession, onNotFound]);

  async function handleContinue(): Promise<void> {
    setSubmitting(true);
    const result = token !== null ? await redeemMagicLink(token) : ({ outcome: "not_found" } as const);
    // Removed from history only now, after the POST has run, in every
    // branch (AC-31) -- never on load, so a reload before Continue is
    // pressed does not strand the customer.
    clearFragment();
    setSubmitting(false);

    switch (result.outcome) {
      case "authenticated":
        onRedeemed();
        return;
      case "read_only":
        onReadOnly();
        return;
      case "active_session":
        onActiveSession();
        return;
      case "throttled":
        setView("throttled");
        return;
      case "unavailable":
        setView("couldNotComplete");
        return;
      case "not_found":
        onNotFound();
        return;
    }
  }

  // DEBT-20261015-tp19: the shared header and footer render, with an empty
  // main, for the moment before the effect above redirects (the same pattern
  // as ADR-20261015-j9gq's gated declaration route).
  if (token === null) {
    return <PortalLayout headerVariant="entry" background="white">{null}</PortalLayout>;
  }

  if (view === "throttled") {
    return (
      <PortalLayout headerVariant="entry" helpLine={redeemContent.throttled.helpLine} background="white">
        <Container layout="entry">
          <Stack gap="field">
            <Typography variant="heading1" as="h1">
              {redeemContent.throttled.heading}
            </Typography>
            <Typography variant="body">{redeemContent.throttled.body}</Typography>
          </Stack>
        </Container>
      </PortalLayout>
    );
  }

  if (view === "invalidFormat") {
    return (
      <PortalLayout headerVariant="entry" helpLine={redeemContent.invalidFormat.helpLine} background="white">
        <Container layout="entry">
          <Stack gap="field">
            <Typography variant="display" as="h1">
              {redeemContent.invalidFormat.heading}
            </Typography>
            <Typography variant="bodyLarge">{redeemContent.invalidFormat.body}</Typography>
            <Button emphasis="primary" onClick={onGetNewLink} size="large" fullWidth>
              {redeemContent.invalidFormat.newLinkActionLabel}
            </Button>
          </Stack>
        </Container>
      </PortalLayout>
    );
  }

  if (view === "couldNotComplete") {
    return (
      <PortalLayout headerVariant="entry" background="white">
        <Container layout="entry">
          <Stack gap="field">
            <Typography variant="display" as="h1">
              {redeemContent.couldNotComplete.heading}
            </Typography>
            <Typography variant="bodyLarge">{redeemContent.couldNotComplete.body}</Typography>
          </Stack>
        </Container>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout headerVariant="entry" helpLine={redeemContent.interstitial.helpLine} background="white">
      <Container layout="entry">
        <Stack gap="field">
          <Typography variant="display" as="h1">
            {redeemContent.interstitial.heading}
          </Typography>
          <Typography variant="bodyLarge">{redeemContent.interstitial.body}</Typography>
          <Button emphasis="primary" onClick={() => void handleContinue()} disabled={submitting} size="large" fullWidth>
            {redeemContent.interstitial.continueActionLabel}
          </Button>
        </Stack>
      </Container>
    </PortalLayout>
  );
}
